import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Info, OctagonAlert } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { MockBadge } from "@/components/common/MockBadge";
import { StatusBadge } from "@/components/common/StatusBadge";
import { DataTable, type Column } from "@/components/common/DataTable";
import { SearchInput } from "@/components/common/SearchInput";
import { Filters } from "@/components/common/Filters";
import { EmptyState } from "@/components/common/EmptyState";
import { KpiCard } from "@/components/common/KpiCard";
import { dashboardService } from "@/api/services";
import { useI18n } from "@/i18n/I18nProvider";
import {
  COMMANDE_STATUTS,
  DASHBOARD_RESUME_CURRENCY_KEYS,
  DASHBOARD_RESUME_KEYS,
  type CommandeOuverte,
  type DashboardAlerte,
  type DashboardResume,
  type DashboardResumeKey,
  type Severite,
} from "@/types/domain";
import {
  KPI_TONES,
} from "@/mocks/dashboardMock";
import { cn } from "@/lib/cn";

// Navigations activées uniquement là où la correspondance avec l'API est CERTAINE.
// Les autres KPI restent non cliquables : leur formule appartient au backend
// (ex. commandes_a_preparer peut couvrir RESERVE / EN_PREPARATION / PRET,
// Les deux clés historiques de facturation sont alimentées par le registre
// Phase 6 : expéditions sans facture et factures en attente du retour Sage.
const KPI_LINKS: Partial<Record<DashboardResumeKey, string>> = {
  commandes_a_verifier: "/commandes?statut=A_VERIFIER",
  commandes_a_acheter: "/commandes?statut=A_ACHETER",
};

const SEVERITE_ICON: Record<Severite, typeof Info> = {
  critical: OctagonAlert,
  attention: AlertTriangle,
  info: Info,
};

const SEVERITE_TEXT: Record<Severite, string> = {
  critical: "text-red-600 dark:text-red-400",
  attention: "text-amber-600 dark:text-amber-400",
  info: "text-blue-600 dark:text-blue-400",
};

export default function DashboardPage() {
  const { t, lang } = useI18n();
  const [query, setQuery] = useState("");
  const [statut, setStatut] = useState("ALL");
  const [resume, setResume] = useState<DashboardResume | null>(null);
  const [resumeLoading, setResumeLoading] = useState(true);
  const [resumeError, setResumeError] = useState<string | null>(null);

  const [alertes, setAlertes] = useState<DashboardAlerte[]>([]);
  const [commandesOuvertes, setCommandesOuvertes] = useState<CommandeOuverte[]>([]);

  const locale = lang === "fr" ? "fr-CA" : "en-CA";
  const formatCurrency = (value: number) =>
    new Intl.NumberFormat(locale, {
      style: "currency",
      currency: "CAD",
      maximumFractionDigits: 0,
    }).format(value);

useEffect(() => {
  const loadResume = async () => {
    try {
      setResumeLoading(true);
      setResumeError(null);

      const data = await dashboardService.resume();
      setResume(data);

const alertesData = await dashboardService.alertes();

const alertesFormatees: DashboardAlerte[] = [
  ...alertesData.commandes.map((commande) => ({
    code: "COMMANDE_A_VERIFIER",
    message: `Commande ${commande.numero_commande} en attente de vérification`,
    severite: "attention" as const,
    reference: commande.numero_commande,
  })),
  ...alertesData.stock.map((produit) => ({
    code: "STOCK_FAIBLE",
    message: `${produit.sku} sous le seuil minimal`,
    severite: "critical" as const,
    reference: produit.sku,
  })),
];

setAlertes(alertesFormatees);

const commandesData = await dashboardService.commandesOuvertes();
setCommandesOuvertes(commandesData);

    } catch (error) {
      console.error("Erreur chargement dashboard:", error);
      setResumeError("Impossible de charger les indicateurs du dashboard.");
    } finally {
      setResumeLoading(false);
    }
  };

  loadResume();
}, []);

  const rows = useMemo(
    () =>
      commandesOuvertes.filter((c) => {
        const matchStatut = statut === "ALL" || c.statut === statut;
        const q = query.trim().toLowerCase();
        const matchQuery =
          !q ||
          c.numero_commande.toLowerCase().includes(q) ||
          c.client.nom.toLowerCase().includes(q);
        return matchStatut && matchQuery;
      }),
    [query, statut, commandesOuvertes],
  );

  const columns: Column<CommandeOuverte>[] = [
    {
      key: "numero_commande",
      header: t.dashboard.table.numero,
      mono: true,
      render: (r) => r.numero_commande ?? "—",
    },
    { key: "client", header: t.dashboard.table.client, render: (r) => r.client?.nom ?? "—" },
    { key: "statut", header: t.dashboard.table.statut, render: (r) => <StatusBadge status={r.statut} /> },
    {
  key: "date_reception",
  header: t.dashboard.table.dateRequise,
  mono: true,
  render: (r) =>
    r.date_reception
      ? new Date(r.date_reception).toLocaleDateString(locale)
      : "—",
},
    {
      key: "valeur_commande",
      header: t.dashboard.table.montant,
      align: "right",
      mono: true,
      render: (r) => (r.valeur_commande == null ? "—" : formatCurrency(r.valeur_commande)),
    },
  ];
if (resumeLoading) {
  return <div>Chargement du dashboard...</div>;
}

if (resumeError || !resume) {
  return <div>{resumeError ?? "Impossible de charger le dashboard."}</div>;
}

  return (
    <div data-testid="dashboard-page" className="space-y-6">
      <PageHeader
        title={t.dashboard.title}
        subtitle={t.dashboard.subtitle}
        breadcrumb={[t.appSuffix, t.dashboard.title]}
      />

      <section data-testid="kpi-section" className="space-y-2">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5">
          {DASHBOARD_RESUME_KEYS.map((key) => {
            const isCurrency = DASHBOARD_RESUME_CURRENCY_KEYS.includes(key);
            return (
              <KpiCard
                key={key}
                testId={`kpi-card-${key}`}
                label={t.dashboard.kpi[key]}
                value={isCurrency ? formatCurrency(resume[key]) : resume[key]}
                tone={KPI_TONES[key]}
                to={KPI_LINKS[key]}
                compact={isCurrency}
              />
            );
          })}
        </div>
        <p className="text-xs text-muted-foreground">{t.dashboard.kpiNote}</p>
      </section>

      <div className="grid gap-6 xl:grid-cols-3">
        <section data-testid="alertes-panel" className="ops-panel xl:col-span-1">
          <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
            <div>
              <h2 className="text-sm font-semibold tracking-tight">{t.dashboard.alertes}</h2>
              <p className="mt-0.5 font-mono text-[11px] text-muted-foreground">
                {t.dashboard.alertesSource}
              </p>
            </div>
          </div>
          {alertes.length === 0 ? (
            <EmptyState title={t.common.empty} description={t.common.emptyHint} />
          ) : (
            <ul className="divide-y divide-border">
              {alertes.map((alerte) => {
                const Icon = SEVERITE_ICON[alerte.severite];
                return (
                  <li
                    key={alerte.code}
                    data-testid={`alerte-item-${alerte.code.toLowerCase()}`}
                    className="flex items-start gap-3 px-4 py-3 transition-colors duration-150 hover:bg-[hsl(var(--surface-raised))]"
                  >
                    <Icon className={cn("mt-0.5 h-4 w-4 shrink-0", SEVERITE_TEXT[alerte.severite])} />
                    <div className="min-w-0">
                      <p className="text-sm font-medium">{alerte.message}</p>
                      <p className="mt-0.5 font-mono text-[11px] text-muted-foreground">
                        {alerte.code}
                        {alerte.reference ? ` · ${alerte.reference}` : ""}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <section data-testid="commandes-ouvertes-panel" className="ops-panel xl:col-span-2">
          <div className="flex flex-col gap-3 border-b border-border px-4 py-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h2 className="flex items-center gap-2 text-sm font-semibold tracking-tight">
                {t.dashboard.commandesOuvertes}
              </h2>
              <p className="mt-0.5 font-mono text-[11px] text-muted-foreground">
                {t.dashboard.commandesSource}
              </p>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <SearchInput
                value={query}
                onChange={setQuery}
                placeholder={t.common.search}
                className="sm:w-52"
                testId="commandes-search"
              />
              <Filters
                label={t.common.filters}
                value={statut}
                onChange={setStatut}
                testId="commandes-filters"
                options={[
                  { value: "ALL", label: t.common.allStatuses },
                  ...COMMANDE_STATUTS.map((s) => ({ value: s, label: s.replace(/_/g, " ") })),
                ]}
              />
            </div>
          </div>
          {rows.length === 0 ? (
            <EmptyState title={t.common.empty} description={t.common.emptyHint} />
          ) : (
            <DataTable
              columns={columns}
              rows={rows}
              rowKey={(r) => r.id}
              testId="commandes-ouvertes-table"
            />
          )}
        </section>
      </div>
    </div>
  );
}
